/**
 * EditEntryBody — the panel CONTENTS for the edit-entry experience.
 *
 * Contains everything inside the panel: the header, export-error banner,
 * attorney-feedback banner, EntryForm, and all the state/effects/handlers
 * (Escape-to-close, feedback resolution, save/export/duplicate wiring).
 *
 * Deliberately decoupled from positioning AND chrome so multiple consumers can
 * reuse it inside a shadcn `Dialog`:
 *   - the attorney edit dialog renders it as the sole `DialogContent` child.
 *   - `InternalEditEntry` (internal dashboard) renders it as the left column of
 *     a split-pane `DialogContent`, alongside contextual panels.
 *
 * The Dialog owns the overlay, the panel chrome (border/bg/rounded/shadow), the
 * top-right close button, and Escape/backdrop dismissal. This body is pure
 * content; consumers pass `className` for layout and wire `onClose` for the
 * post-action (save/export/delete) dismiss.
 */

import { format, parse } from "date-fns";
import { AlertTriangle, MessageSquareWarning } from "lucide-react";
import * as React from "react";
import type { Activity, Matter, Task } from "@/shared/cache";
import type { Abbreviation } from "@/shared/components/NarrativeTextArea/NarrativeTextArea";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { formatTime } from "@/shared/utils/formatTime";
import { EntryForm } from "./EntryForm";
import type { TimeEntryFormValues } from "./schema";

// ── Types ──────────────────────────────────────────────────────────

export type { Activity, Matter, Task };

export type SelectedRowData = {
	id: number;
	date: string;
	matter_name: string | null;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
	document_name?: string;
	units_seconds?: number;
	start_time?: string;
	end_time?: string;
	application?: string;
	original_description?: string | null;
	original_units?: number | null;
	original_matter?: string | null;
	original_task?: string | null;
	original_activity?: string | null;
	attorney_feedback?: string | null;
};

export type EditEntryBodyProps = {
	onSave: (
		entry: {
			id: number;
			date: string;
			matter: string | null;
			matter_id: string;
			description: string;
			duration: number;
			task: string;
			activity: string;
		},
		shouldExport: boolean,
	) => void;
	onDuplicate?: (
		entry: {
			date: string;
			matter: string | null;
			matter_id: string;
			description: string;
			duration: number;
			task: string;
			activity: string;
			document_name: string | null;
			client: string | null;
			units_seconds: number;
			start_time: string | null;
			end_time: string | null;
		},
		originalId: number,
	) => Promise<void>;
	onDelete?: (id: number) => Promise<void> | void;
	matters: Matter[];
	tasks: Task[];
	activities: Activity[];
	selectedRowData: SelectedRowData;
	abbreviations?: Abbreviation[];
	attorneyFeedback?: string | null;
	onResolveFeedback?: () => Promise<void>;
	exportError?: string | null;
	/**
	 * Called after a successful save/export/delete to dismiss the dialog. The
	 * Dialog itself owns close-button/Escape/backdrop dismissal via
	 * `onOpenChange`, so this is only the post-action path.
	 */
	onClose: () => void;
	/** Layout classes for the content root (padding, sizing, scroll, borders). */
	className?: string;
};

// ── Helpers ────────────────────────────────────────────────────────

function parseEntryDate(dateStr: string): Date {
	if (!dateStr) return new Date();
	const parsed = parse(dateStr, "MM/dd/yyyy", new Date());
	if (!Number.isNaN(parsed.getTime())) return parsed;
	const fallback = new Date(dateStr);
	return Number.isNaN(fallback.getTime()) ? new Date() : fallback;
}

// ── Component ──────────────────────────────────────────────────────

export function EditEntryBody({
	onSave,
	onDuplicate,
	onDelete,
	matters,
	tasks,
	activities,
	selectedRowData,
	abbreviations,
	attorneyFeedback,
	onResolveFeedback,
	exportError,
	onClose,
	className,
}: EditEntryBodyProps) {
	const entryId = selectedRowData.id;
	const [isResolvingFeedback, setIsResolvingFeedback] = React.useState(false);
	const [feedbackDismissed, setFeedbackDismissed] = React.useState(false);

	// Reset feedback dismissed state when entry changes
	React.useEffect(() => {
		void entryId;
		setFeedbackDismissed(false);
	}, [entryId]);

	// ── Build default values from selectedRowData ─────────────────

	const defaultValues = React.useMemo(
		() => ({
			date: parseEntryDate(selectedRowData.date),
			matter_id: selectedRowData.matter_id || "",
			matter: selectedRowData.matter_name ?? null,
			task: selectedRowData.task || "",
			activity: selectedRowData.activity || "",
			duration: selectedRowData.duration || 0,
			description: selectedRowData.description || "",
		}),
		[selectedRowData],
	);

	// ── Handlers ──────────────────────────────────────────────────

	const toSaveEntry = React.useCallback(
		(values: TimeEntryFormValues) => ({
			id: entryId,
			date: values.date ? format(values.date, "MM/dd/yyyy") : "",
			matter: values.matter,
			matter_id: values.matter_id,
			description: values.description,
			duration: values.duration,
			task: values.task,
			activity: values.activity,
		}),
		[entryId],
	);

	const handleSave = React.useCallback(
		(values: TimeEntryFormValues) => {
			onSave(toSaveEntry(values), false);
			onClose();
		},
		[onSave, onClose, toSaveEntry],
	);

	const handleExport = React.useCallback(
		(values: TimeEntryFormValues) => {
			onSave(toSaveEntry(values), true);
			onClose();
		},
		[onSave, onClose, toSaveEntry],
	);

	const handleDuplicate = React.useCallback(
		async (values: TimeEntryFormValues) => {
			if (!onDuplicate) return;
			const selectedMatter = matters.find(
				(m) => m.matter_id === values.matter_id,
			);
			await onDuplicate(
				{
					date: values.date ? format(values.date, "MM/dd/yyyy") : "",
					matter: values.matter,
					matter_id: values.matter_id,
					description: values.description,
					duration: values.duration,
					task: values.task,
					activity: values.activity,
					document_name: selectedRowData.document_name || null,
					client: selectedMatter?.client || null,
					units_seconds: selectedRowData.units_seconds || 0,
					start_time: selectedRowData.start_time || null,
					end_time: selectedRowData.end_time || null,
				},
				entryId,
			);
		},
		[onDuplicate, matters, selectedRowData, entryId],
	);

	const handleDelete = React.useCallback(() => {
		if (!onDelete) return;
		onClose();
		void onDelete(entryId);
	}, [onDelete, onClose, entryId]);

	const handleResolveFeedback = React.useCallback(async () => {
		if (!onResolveFeedback) return;
		setIsResolvingFeedback(true);
		try {
			await onResolveFeedback();
			setFeedbackDismissed(true);
		} finally {
			setIsResolvingFeedback(false);
		}
	}, [onResolveFeedback]);

	const showFeedback = attorneyFeedback && !feedbackDismissed;

	// ── Render ─────────────────────────────────────────────────────

	return (
		<div className={cn("text-foreground", className)}>
			{/* Header */}
			<div className="mb-4">
				<h2 className="text-lg font-sans font-semibold leading-none tracking-tight">
					Edit entry
				</h2>
				<p className="mt-1.5 text-sm text-muted-foreground">
					{selectedRowData.matter_id}
				</p>
				{selectedRowData.end_time && (
					<p className="mt-0.5 text-xs text-muted-foreground">
						{formatTime(selectedRowData.end_time)}
					</p>
				)}
			</div>

			{/* Export error banner */}
			{exportError && (
				<div className="mb-4 rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30 p-3">
					<div className="flex items-start gap-2">
						<AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
						<div className="flex-1 min-w-0">
							<p className="text-sm font-medium text-red-800 dark:text-red-300">
								Export Failed
							</p>
							<p className="mt-1 text-sm text-red-700 dark:text-red-400/80">
								{exportError}
							</p>
						</div>
					</div>
				</div>
			)}

			{/* Attorney feedback banner */}
			{showFeedback && (
				<div className="mb-4 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 p-3">
					<div className="flex items-start gap-2">
						<MessageSquareWarning className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
						<div className="flex-1 min-w-0">
							<p className="text-sm font-medium text-amber-800 dark:text-amber-300">
								Admin Feedback
							</p>
							<p className="mt-1 text-sm text-amber-700 dark:text-amber-400/80">
								{attorneyFeedback}
							</p>
						</div>
						{onResolveFeedback && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={handleResolveFeedback}
								disabled={isResolvingFeedback}
								className="shrink-0 h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/50"
							>
								{isResolvingFeedback ? "Resolving..." : "Resolve"}
							</Button>
						)}
					</div>
				</div>
			)}

			{/* Form — key forces full remount when switching entries */}
			<EntryForm
				key={entryId}
				matters={matters}
				tasks={tasks}
				activities={activities}
				currentDate={defaultValues.date}
				defaultValues={defaultValues}
				onSubmit={handleSave}
				onExport={handleExport}
				onDuplicate={onDuplicate ? handleDuplicate : undefined}
				onDelete={onDelete ? handleDelete : undefined}
				submitLabel="Save changes"
				exportLabel="Save and export"
				documentName={selectedRowData.document_name || undefined}
				abbreviations={abbreviations}
				validateOnMount
				originals={{
					matter_id: selectedRowData.original_matter,
					task: selectedRowData.original_task,
					activity: selectedRowData.original_activity,
					duration: selectedRowData.original_units,
					description: selectedRowData.original_description,
				}}
			/>
		</div>
	);
}
